import { type ReactNode, useState } from 'react';
import type { RescueDocument, RescueRequiredDocument } from '../../lib/documentQueries';
import { C3, letterName, stateName, uploadedJustNow } from '../../lib/documentLabels';
import { FileButton, FileCard } from '../Documents/FileCard';
import StatusPill, { type PillTone } from '../Documents/StatusPill';
import UploadBox from '../Documents/UploadBox';
import { formatDate, formatFileSize } from '../../lib/utils';
import styles from './OrderDocumentsStep.module.scss';

const TEOS_SEARCH_URL = 'https://apps.irs.gov/app/eos/';
const F0003_URL =
  'https://www.streamlinedsalestax.org/docs/default-source/forms/exemption-certificateb926a7ab4a0d43e1ad4fe8eb19e79cbb.pdf?sfvrsn=857843d_5';

/** An SST state names the form it takes; everywhere else the certificate varies, so it is described. */
function documentLabel(entry: RescueRequiredDocument, government: boolean): string {
  if (entry.documentType === 'NONPROFIT_LETTER') {
    return letterName(government);
  }
  if (entry.bucket === 'MULTISTATE_F0003') {
    return 'Certificate of Exemption (Form F0003)';
  }
  return entry.state ? `${stateName(entry.state)} sales tax exemption certificate` : 'Sales tax exemption certificate';
}

/** Why it is being asked for, or that it is already answered. */
function documentSublabel(entry: RescueRequiredDocument, letterPrograms: string, multiple: boolean): string {
  const doc = entry.document;
  // Filed before this step opened. One uploaded here is still the thing being asked for.
  if (
    entry.documentType === 'NONPROFIT_LETTER' &&
    doc &&
    doc.status !== 'REJECTED' &&
    !doc.expired &&
    !uploadedJustNow(doc)
  ) {
    return 'Already on file';
  }
  if (entry.documentType === 'NONPROFIT_LETTER') {
    // With two documents in the step the programs are already named in the intro.
    return multiple ? 'Required' : `Required for ${letterPrograms}`;
  }
  return entry.state ? `Required for shipments to ${stateName(entry.state)}` : 'Required';
}

/** What the jurisdiction takes, in its own terms. Null when there is nothing extra to say. */
function certificateHint(entry: RescueRequiredDocument): ReactNode {
  const where = entry.state ? stateName(entry.state) : 'This state';
  if (entry.bucket === 'MULTISTATE_F0003') {
    return (
      <>
        {where} is a Streamlined Sales Tax state, so use the multistate Certificate of Exemption. List{' '}
        <b>Barking Labs Corp.</b> as the seller, at 408 Broadway, FL 3, New York, NY 10013.
        <br />
        <a href={F0003_URL} target="_blank" rel="noreferrer">
          Download Form F0003
        </a>
      </>
    );
  }
  if (entry.bucket === 'STATE_CERTIFICATE') {
    const issuer = entry.issuingAgency ? ` Your state issues it through the ${entry.issuingAgency}.` : '';
    return `${where} isn't a Streamlined Sales Tax state, so upload the sales tax exemption certificate your state issued to your organization.${issuer}`;
  }
  return null;
}

function uploadPrompt(entry: RescueRequiredDocument, teos: boolean, government: boolean): string {
  const doc = entry.document;
  if (entry.documentType !== 'NONPROFIT_LETTER') {
    const form = entry.bucket === 'MULTISTATE_F0003' ? 'Form F0003' : 'state exemption certificate';
    if (doc?.expired) {
      return `Upload your renewed ${form}`;
    }
    if (doc) {
      return 'Upload a replacement certificate';
    }
    return entry.bucket === 'MULTISTATE_F0003' ? 'Upload your completed Form F0003' : `Upload your ${form}`;
  }
  if (doc) {
    return 'Upload a replacement letter';
  }
  if (government) {
    return 'Upload proof of government status';
  }
  return teos ? 'Upload your TEOS printout' : 'Upload your IRS determination letter';
}

const GOVERNMENT_EXAMPLES =
  'For example: a letter on municipal letterhead signed by a department head, the ordinance that established your animal control function, a government contract naming you the official animal control agency, a state agency designation letter, or a budget document listing your shelter.';

function uploadHint(entry: RescueRequiredDocument, teos: boolean, compact: boolean, government: boolean): string {
  if (compact || entry.documentType !== 'NONPROFIT_LETTER' || teos) {
    return 'PDF up to 20MB.';
  }
  if (government) {
    return `${GOVERNMENT_EXAMPLES} PDF up to 20MB.`;
  }
  return `The letter the IRS sent confirming your organization's ${C3} status. PDF up to 20MB.`;
}

/** What the reviewer has decided so far, as the pill beside the file. */
function statusPill(doc: RescueDocument): { text: string; tone: PillTone } {
  if (doc.status === 'REJECTED') {
    return { text: 'Not accepted', tone: 'rejected' };
  }
  if (doc.status === 'PENDING') {
    return { text: 'In review', tone: 'pending' };
  }
  if (doc.expired && doc.expirationDate) {
    return { text: `Expired ${formatDate(doc.expirationDate)}`, tone: 'rejected' };
  }
  return { text: 'Approved', tone: 'approved' };
}

/** The second line of a filed document's card. The pill beside it carries the review state. */
function fileSubtitle(doc: RescueDocument, isLetter: boolean): string {
  if (doc.status === 'REJECTED') {
    return `Reviewed ${formatDate(doc.reviewedAt ?? doc.uploadedAt)}`;
  }
  if (doc.expired) {
    return `Approved ${formatDate(doc.reviewedAt ?? doc.uploadedAt)}`;
  }
  if (uploadedJustNow(doc)) {
    return `${formatFileSize(doc.sizeBytes)} · just now`;
  }
  return isLetter ? `On file · uploaded ${formatDate(doc.uploadedAt)}` : `Uploaded ${formatDate(doc.uploadedAt)}`;
}

/** Nothing usable is on file: never filed, turned down, or lapsed. On file but unreviewed is enough. */
export function needsAnswer(entry: RescueRequiredDocument): boolean {
  const doc = entry.document;
  return !entry.satisfied && (doc === null || doc.status === 'REJECTED' || doc.expired);
}

/** Whether the rescue has answered every document this order asks for. */
export function documentsAnswered(required: RescueRequiredDocument[]): boolean {
  return !required.some(needsAnswer);
}

/**
 * The documents this order needs, in the order flow rather than on the account page.
 *
 * Everything asked for is listed, approved included: a rescue reading the step should see that the
 * letter is already answered rather than wonder where it went. A first order goes through with its
 * documents merely on file, which the server decides — this renders what it returned.
 */
export default function OrderDocumentsStep({
  required,
  uploadingKey,
  keyFor,
  onPickFile,
  fileError,
  intro,
  letterPrograms,
  government,
  waitNote,
  notice,
}: {
  required: RescueRequiredDocument[];
  uploadingKey: string | null;
  keyFor: (documentType: RescueRequiredDocument['documentType'], state: string | null) => string;
  onPickFile: (documentType: RescueRequiredDocument['documentType'], state: string | null) => void;
  fileError: string | null;
  /** Why this order needs documents, which differs between a Mini order and a Nano shipment. */
  intro: string;
  /** The programs the federal letter unlocks here, e.g. "Fi Minis". */
  letterPrograms: string;
  government: boolean;
  /** Shown under a document in review when this order can't go until it is approved. */
  waitNote?: string | null;
  /** Why no upload will let this order through, when the jurisdiction itself refuses it. */
  notice?: string | null;
}) {
  const [lostLetter, setLostLetter] = useState(false);
  // One document gets the room to explain itself; two share the step, so each one gets a compact row.
  const compact = required.length > 1;

  return (
    <div className={styles.documentsStep}>
      <p className={styles.lead}>{intro}</p>

      {notice && <div className={styles.error}>{notice}</div>}

      {required.map((entry) => {
        const uploading = uploadingKey === keyFor(entry.documentType, entry.state);
        const doc = entry.document;
        const isLetter = entry.documentType === 'NONPROFIT_LETTER';
        const teos = isLetter && lostLetter && !government;
        const needsUpload = doc === null || doc.status === 'REJECTED' || doc.expired;
        const pill = doc ? statusPill(doc) : null;
        const hint = certificateHint(entry);
        // Only a file filed in this step offers Replace here; anything older is replaced from the account page.
        const replaceable = doc?.status === 'PENDING' && uploadedJustNow(doc);

        return (
          <div className={styles.doc} key={keyFor(entry.documentType, entry.state)}>
            <div className={styles.docHead}>
              <span className={styles.docName}>{documentLabel(entry, government)}</span>
              <span className={styles.docFor}>{documentSublabel(entry, letterPrograms, compact)}</span>
            </div>

            {/* How to fill the form in, which stops mattering once one is on file. */}
            {!isLetter && hint && needsUpload && <p className={styles.docHint}>{hint}</p>}

            {doc && (
              <FileCard name={doc.originalFileName} sub={fileSubtitle(doc, isLetter)}>
                {pill && !replaceable && <StatusPill tone={pill.tone}>{pill.text}</StatusPill>}
                {replaceable && (
                  <FileButton onClick={() => onPickFile(entry.documentType, entry.state)} disabled={uploading}>
                    {uploading ? 'Uploading…' : 'Replace'}
                  </FileButton>
                )}
              </FileCard>
            )}

            {doc?.status === 'REJECTED' && doc.rejectionReason && (
              <p className={styles.rejectNote}>Reason: {doc.rejectionReason}</p>
            )}

            {teos && needsUpload && (
              <div className={`${styles.teos} ${compact ? styles.teosCompact : ''}`}>
                {compact ? (
                  <p className={styles.teosLead}>
                    Lost your letter? Upload a printout from the IRS Tax Exempt Organization Search (TEOS) instead. It
                    must show your organization's legal name, your EIN, and a search date within 90 days of upload.{' '}
                    <a href={TEOS_SEARCH_URL} target="_blank" rel="noreferrer">
                      Search TEOS on irs.gov
                    </a>
                  </p>
                ) : (
                  <>
                    <p className={styles.teosLead}>
                      Lost your letter? Upload a printout from the IRS Tax Exempt Organization Search (TEOS) instead. It
                      must show:
                    </p>
                    <ul className={styles.teosList}>
                      <li>Your organization's legal name</li>
                      <li>Your EIN</li>
                      <li>The date of the search, within 90 days of when you upload it</li>
                    </ul>
                    <a className={styles.teosLink} href={TEOS_SEARCH_URL} target="_blank" rel="noreferrer">
                      Search TEOS on irs.gov
                    </a>
                  </>
                )}
              </div>
            )}

            {needsUpload && (
              <UploadBox
                compact={compact || teos}
                title={uploadPrompt(entry, teos, government)}
                hint={uploadHint(entry, teos, compact, government)}
                onUpload={() => onPickFile(entry.documentType, entry.state)}
                uploading={uploading}
              />
            )}

            {/* The documents rescues most often send by mistake, named before they send one. */}
            {isLetter && needsUpload && !teos && !government && (
              <p className={styles.caution}>
                {`Tax returns (Form 990), W-9 forms, and nonprofit incorporation certificates aren't accepted. Upload the letter the IRS sent confirming your ${C3} status.`}
              </p>
            )}

            {isLetter && needsUpload && !government && (
              <button type="button" className={styles.fallbackLink} onClick={() => setLostLetter(!teos)}>
                {teos ? 'I have my determination letter' : `I've lost my IRS ${C3} letter`}
              </button>
            )}

            {replaceable && !waitNote && (
              <p className={styles.fileNote}>Ready to go. The Fi team reviews it after you place this order.</p>
            )}
            {doc?.status === 'PENDING' && !needsUpload && waitNote && !isLetter && (
              <p className={styles.fileNote}>{waitNote}</p>
            )}
          </div>
        );
      })}

      {fileError && <div className={styles.error}>{fileError}</div>}
    </div>
  );
}
